import { useState } from "react";
import styled, { css } from "styled-components";
import { useQueryClient } from "@tanstack/react-query";
import { useSkipioApi } from "../../api";
import useMoment, { moment } from "../../hooks/use-moment";
import { useToast } from "../../hooks/use-toast";
import {
  ERROR_COLOR,
  SUCCESS_COLOR_LIGHT,
  THEME_COLOR,
  THEME_COLOR_DARK,
} from "../../vars";
import {
  Box,
  BoxContent,
  BoxFooter,
  BoxHeader,
  BoxNavButton,
  BoxNavLeft,
  BoxNavRight,
  BoxTitle,
} from "../Box";
import Button from "../Button";
import Checkbox from "../Checkbox";
import Composer, { TMessage } from "../Composer";
import { Close } from "../Icons";
import { HelpText, Label } from "../Input";
import { Container, MediaMessage, SmsMessage } from "../Message";
import Overlay, { OverlayTrigger } from "../Overlay";
import SidePanel, { SidePanelTrigger } from "../SidePanel";
import Stack from "../Stack";
import Title from "../Title";
import { optOutInstructions } from "./constants";
import { TKeyword } from "./KeywordCard";

function MsgKeyword({
  hide,
  keyword,
}: {
  hide: () => void;
  keyword?: TKeyword;
}) {
  const moment = useMoment();
  const [errors, setErrors] = useState([]);
  const [includeOptOut, setIncludeOptOut] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const skipioApi = useSkipioApi();
  const queryClient = useQueryClient();
  const showToast = useToast();

  const initialState: TMessage = {
    body: "",
    voicemailId: null,
  };
  const [value, onChange] = useState(initialState);

  const cancel = () => {
    setIncludeOptOut(false);
    onChange(initialState);
  };

  if (errors.length) console.log(errors);

  const sendMessage = async (message?: TMessage, closeOverlay?: () => void) => {
    if (!message) return;
    setErrors([]);
    const caller = skipioApi.scheduled_messages.post;

    try {
      await caller(
        {},
        {
          data: {
            scheduledMessage: {
              body: message.body,
              deliver_at: message.deliverAt
                ? new Date(message.deliverAt).toISOString()
                : undefined,
              media_urls: [message.imageUrl],
              media_mime_types: [message.imageType],
              message_type: "SmsMessage",
              keyword_ids: [keyword?.id],
            },
          },
        }
      );
      await queryClient.invalidateQueries({ queryKey: ["messages"] });
      if (closeOverlay) closeOverlay();
      setShowSuccess(true);
    } catch (err: any) {
      if (err.response) {
        setErrors(err.response.data.errors);
      } else throw err;
      showToast("Something went wrong", { error: true });
      if (closeOverlay) closeOverlay();
    }
  };

  const toggleOptOutInclusion = () => {
    setIncludeOptOut(!includeOptOut);
  };

  const optOutInstMessage = optOutInstructions(
    keyword?.staticBodyChunk,
    keyword?.messagesPerMonth,
    keyword?.slug
  );
  const authorInfo = value.deliverAt ? (
    <>
      This message will start delivering at{" "}
      <b>
        {moment(value.deliverAt).format("LT")} on{" "}
        {moment(value.deliverAt).format("LL")}
      </b>
    </>
  ) : (
    <>
      This message will start delivering when the messages are successfully
      queued.
    </>
  );
  const messageBody = includeOptOut
    ? `${value.body}\n\n${optOutInstMessage}`
    : value.body;

  return (
    <>
      <BoxHeader>
        <BoxTitle>
          Send a message{keyword?.keyword && ` to ${keyword?.keyword}`}
        </BoxTitle>
        <BoxNavRight>
          <BoxNavButton onClick={hide}>
            <Close />
          </BoxNavButton>
        </BoxNavRight>
      </BoxHeader>
      <BoxContent>
        <Stack>
          <Title center color={THEME_COLOR}>
            {keyword?.activeSubscriberCount} subscribers
          </Title>
          <>
            <Label>Message</Label>
            <Composer
              value={value}
              onChange={onChange}
              enableMessageLibrary={false}
              enableRinglessVoicemail={false}
              hideVideo
              optOutInstruction={
                includeOptOut ? `\n\n${optOutInstMessage}` : ""
              }
            />
          </>
          <>
            <Checkbox
              name="includeOptOut"
              onChange={toggleOptOutInclusion}
              value={includeOptOut}
            >
              <div>Include Opt Out Instructions?</div>
              <HelpText noIcon style={{ fontStyle: "italic" }}>
                Opt-out instructions must be provided at regular intervals and
                at least once per month
              </HelpText>
            </Checkbox>
          </>
          {(value.body || includeOptOut) && (
            <>
              <Label underline>Message Preview</Label>
              <SmsMessage metadata={authorInfo}>{messageBody}</SmsMessage>
            </>
          )}
        </Stack>
      </BoxContent>
      <BoxFooter>
        <Stack horizontal center>
          <BoxNavLeft>
            <Button variant="link" onClick={cancel}>
              Cancel
            </Button>
          </BoxNavLeft>
          <BoxNavRight>
            <ConfirmSendOverlay
              trigger={({ ref, show }) => (
                <Button ref={ref} disabled={!value.body} onClick={show}>
                  Confirm
                </Button>
              )}
              message={{ ...value, body: messageBody }}
              keywordInfo={keyword ? [keyword] : []}
              sendMessage={sendMessage}
            />
          </BoxNavRight>
        </Stack>
      </BoxFooter>
      <Overlay
        isShowing={showSuccess}
        setShowing={setShowSuccess}
        onHide={hide}
        content={({ hide }) => (
          <Box>
            <BoxHeader>
              <BoxTitle>
                {value.deliverAt
                  ? "Your Message is Scheduled"
                  : "Your Message is Queued for Sending"}
              </BoxTitle>
              <BoxNavRight>
                <BoxNavButton onClick={hide}>
                  <Close />
                </BoxNavButton>
              </BoxNavRight>
            </BoxHeader>
            <BoxContent>
              <ConfimContentSection center>
                <Stack>
                  {value.deliverAt ? (
                    <Emoji>&#9202;</Emoji>
                  ) : (
                    <Emoji>&#128077;</Emoji>
                  )}
                  <strong>Nice work!</strong>
                  <p>
                    Your message is queued and is preparing to send. It will
                    begin sending to the {keyword?.activeSubscriberCount}{" "}
                    subscriber(s) in your <strong>{keyword?.keyword}</strong>{" "}
                    keyword group{" "}
                    {value.deliverAt
                      ? `at ${moment(value.deliverAt).format("LT")} on ${moment(
                          value.deliverAt
                        ).format("LL")}`
                      : "when it's ready."}
                  </p>
                </Stack>
              </ConfimContentSection>
            </BoxContent>
          </Box>
        )}
        trigger={({ ref, show }) => <></>}
      />
    </>
  );
}

export function ConfirmSendOverlay({
  trigger,
  message,
  keywordInfo = [],
  sendMessage,
}: {
  trigger: OverlayTrigger;
  message: TMessage;
  keywordInfo?: TKeyword[];
  sendMessage: (message?: TMessage, closeOverlay?: () => void) => void;
}) {
  const multipleSegmentsWarning =
    "Longer messages may be counted as multiple segments.";

  const authorInfo = message.deliverAt ? (
    <>
      This message will start delivering at{" "}
      <b>
        {moment(message.deliverAt).format("LT")} on{" "}
        {moment(message.deliverAt).format("LL")}
      </b>
      <br />
      {multipleSegmentsWarning}
    </>
  ) : (
    <>
      This message will start delivering when the messages are successfully
      queued.
      <br />
      {multipleSegmentsWarning}
    </>
  );

  let subscriberCount = 0;
  if (keywordInfo.length > 0) {
    keywordInfo.forEach((keyword) => {
      subscriberCount += keyword?.activeSubscriberCount || 0;
    });
  }

  return (
    <Overlay
      trigger={trigger}
      content={({ hide }) => (
        <Box elevated>
          <BoxHeader>
            <BoxTitle>Confirm and Send your message:</BoxTitle>
          </BoxHeader>
          <BoxContent>
            {keywordInfo?.length > 0 && (
              <TitleSection>
                <KeyTitle>
                  {keywordInfo
                    .slice(0, 2)
                    .map(
                      (keyword, index) =>
                        `${index > 0 ? ", " : ""}${keyword.keyword}`
                    )}
                  {keywordInfo.length > 2 && ` +${keywordInfo.length - 2} more`}
                </KeyTitle>
                <KeyLabel>{subscriberCount} subscribers</KeyLabel>
              </TitleSection>
            )}
            <Label underline>Message Preview:</Label>
            {message.imageType && message.imageUrl ? (
              <>
                <MediaMessage
                  mediaType={message.imageType}
                  mediaUrl={message.imageUrl}
                  stackedWithNextMessage={!!message.body}
                />
                {message.body && (
                  <SmsMessage
                    metadata={authorInfo}
                    stackedWithPrevMessage={true}
                  >
                    {message.body}
                  </SmsMessage>
                )}
              </>
            ) : (
              <SmsMessage metadata={authorInfo}>{message.body}</SmsMessage>
            )}
          </BoxContent>
          <BoxFooter>
            <BoxNavLeft>
              <Button onClick={hide} style={{ backgroundColor: ERROR_COLOR }}>
                Wait, I want to edit
              </Button>
            </BoxNavLeft>
            <BoxNavRight>
              <Button
                onClick={() => {
                  sendMessage(message, hide);
                }}
                style={{ backgroundColor: SUCCESS_COLOR_LIGHT }}
              >
                Confirm and Send
              </Button>
            </BoxNavRight>
          </BoxFooter>
        </Box>
      )}
    />
  );
}

function MsgKeywordSidePanel({
  trigger,
  keyword,
}: {
  trigger: SidePanelTrigger;
  keyword?: TKeyword;
}) {
  return (
    <SidePanel
      trigger={trigger}
      content={({ hide }) => <MsgKeyword hide={hide} keyword={keyword} />}
    />
  );
}

export const ConfimContentSection = styled.div<{ center?: boolean }>`
  margin: 0 30px;
  ${(p) =>
    p.center
      ? css`
          text-align: center;
        `
      : css``};
  ${Container} {
    text-align: left;
  }
  strong {
    font-weight: bold;
  }
  p {
    line-height: 20px;
  }
`;

const TitleSection = styled.div`
  flex: 1;
  padding-bottom: 10px;
  margin-bottom: 10px;
  width: 100%;
`;

const KeyTitle = styled.div`
  font-size: 25px;
  margin-top: 0px;
  margin-bottom: 5px;
  line-height: 1.2em;
  font-weight: bold;
  color: ${THEME_COLOR_DARK};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const KeyLabel = styled.div`
  font-weight: 500;
  color: #0008;
`;

export const Emoji = styled.div`
  font-size: 100px;
`;

export default MsgKeywordSidePanel;
